import { useTranslation } from 'react-i18next';
import { Avatar } from '@components/ui/Avatar/Avatar';
import type { ChannelSummary } from '@interfaces/ICommsApi';
import { formatRecentTime } from '@utils/formatRecentTime';
import styles from './ChatBox.module.css';

interface IChatBoxProps {
  channel: ChannelSummary;
  label: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasUnread?: boolean;
  /**
   * Exact number of unread messages tallied since the channel was last
   * opened. 0 with `hasUnread=true` means "we know it's unread but lost
   * the count" (e.g. activity arrived while the WS was offline) — render
   * a numberless dot in that case.
   */
  unreadCount?: number;
}

// An attachment-only message's preview marker → its icon and label.
const ATTACHMENT_PREVIEWS: Record<string, { emoji: string; key: string }> = {
  '[image]': { emoji: '📷', key: 'comms.channels.preview.image' },
  '[voice]': { emoji: '🎤', key: 'comms.channels.preview.voice' },
  '[file]': { emoji: '📎', key: 'comms.channels.preview.file' },
};

export function ChatBox({
  channel,
  label,
  selectedId,
  onSelect,
  hasUnread = false,
  unreadCount = 0,
}: IChatBoxProps) {
  const { t, i18n } = useTranslation();
  const activityAt = channel.lastMessageAt ?? channel.createdAt;
  const timeLabel = formatRecentTime(activityAt, i18n.language, t);
  const previewText = (text: string) => {
    const attachment = ATTACHMENT_PREVIEWS[text];
    return attachment ? `${attachment.emoji} ${t(attachment.key)}` : text;
  };
  const countLabel = unreadCount > 99 ? '99+' : String(unreadCount);  // Cap displayed count so a long burst can't widen the badge past the row.

  return (
    <button
      type="button"
      className={`${styles.item} ${selectedId === channel.id ? styles.itemActive : ''} ${hasUnread ? styles.itemUnread : ''}`}
      onClick={() => onSelect(channel.id)}
    >
      <Avatar name={label} glyph={channel.kind === 'channel' ? '#' : undefined} size={25} tinted className={styles.avatar} />
      <div className={styles.itemBody}>
        <div className={styles.itemTopRow}>
          <span className={styles.itemName}>{label}</span>
          <span className={styles.itemTime}>{timeLabel}</span>
          {hasUnread && unreadCount > 0 && (
            <span
              className={styles.unreadCountBadge}
              aria-label={t('comms.channels.unreadCount', { count: unreadCount })}
            >
              {countLabel}
            </span>
          )}
          {hasUnread && unreadCount === 0 && (
            <span
              className={styles.unreadDot}
              aria-label={t('comms.channels.unread')}
            />
          )}
        </div>
        <div className={styles.itemSubtitle}>
          {channel.lastMessagePreview ? (
            <>
              <span className={styles.previewAuthor}>
                {channel.lastMessagePreview.authorName}:
              </span>{' '}
              {previewText(channel.lastMessagePreview.text)}
            </>
          ) : (
            t('comms.channels.noMessages')
          )}
        </div>
      </div>
    </button>
  );
}
